import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SITE, BRAND_IMAGE } from "@/site";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/courses", label: "Courses" },
    { to: "/gallery", label: "Gallery" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = React.useState(false);
    const [scrolled, setScrolled] = React.useState(false);
    const { pathname } = useLocation();

    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    React.useEffect(() => { setOpen(false); }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" data-testid="logo-link" className="group flex items-center gap-3">
                        <span className="relative flex items-center justify-center h-12 w-14 sm:w-16 shrink-0">
                            <img src={BRAND_IMAGE} alt="IHAO MUSIC HOUSE DELHI logo" className="w-full h-full object-contain" />
                        </span>
                        <div className="leading-tight">
                            <div className="font-display font-semibold text-white text-sm sm:text-base whitespace-nowrap tracking-tight">Ihao Music House Delhi</div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    `relative px-4 py-2 text-sm font-medium transition-colors ${
                                        isActive ? "text-gold" : "text-white/80 hover:text-white"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {item.label}
                                        {isActive && (
                                            <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-gold" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="header-book-demo-btn"
                            className="hidden sm:inline-flex items-center gap-2 bg-gold text-black font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-gold-hover hover:scale-[1.03] transition-all duration-300 gold-glow"
                        >
                            Book Free Trial Class
                        </Link>
                        <button
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                            onClick={() => setOpen((o) => !o)}
                            className="lg:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-1.5 text-white"
                        >
                            <span className={`block w-5 h-px bg-white transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`} />
                            <span className={`block w-5 h-px bg-white transition-opacity ${open ? "opacity-0" : "opacity-100"}`} />
                            <span className={`block w-5 h-px bg-white transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            <div
                data-testid="mobile-nav"
                className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ease-out bg-black/95 backdrop-blur-xl border-b border-white/10 ${
                    open ? "max-h-[480px]" : "max-h-0"
                }`}
            >
                <div className="px-6 py-6 flex flex-col gap-1">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-3 rounded-lg text-base font-medium ${
                                    isActive ? "text-gold bg-white/5" : "text-white/80 hover:bg-white/5"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="mobile-book-demo-btn"
                        className="mt-3 inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-5 py-3 rounded-full"
                    >
                        Book Free Trial Class
                    </Link>
                    <a href={`tel:${SITE.phone}`} className="mt-2 px-4 py-2 text-sm text-white/60">Call: {SITE.phoneIntl}</a>
                </div>
            </div>
        </header>
    );
}
